import { Max, Scale } from "@pdfking/math"
import { HorizontalAlign } from "@pdfking/renderer-api"

import { PALETTE } from "#constants"
import type { CalendarDesign } from "#internal"

import { AgendaDate } from "../../agenda/AgendaDate"
import { AgendaWeeks } from "../../agenda/AgendaWeeks"
import { Slice } from "../../layout/Slice"
import { Area } from "../Area"
import { MonthName } from "../MonthName"
import { PictureCover } from "../PictureCover"
import { PlannerWeek } from "../PlannerWeek"
import { ShiftMonth } from "../ShiftMonth"

/** Albertus, a long weekly desk planner, a week a page: the contact at its
 * head, the week's months and their years on its right; its days dated,
 * Monday to Saturday a column each, Sunday under Saturday; at its foot the
 * week's number and its month and the next across in strips, each its name
 * before it. */
export const Albertus: CalendarDesign = {
  cover: PictureCover({}),
  page: ({ gap, language, outer, short, year }, week) => {
    const pitch = Scale(short, 0.067)
    const [head, rest] = Slice.top(outer, Scale(short, 0.1), gap)
    const [foot, days] = Slice.bottom(rest, Scale(short, 0.16), gap)
    const [label, strips] = Slice.left(foot, Scale(foot.width, 0.2), gap)
    // The week's days in the year, from its Monday or 1 January
    const { first } = AgendaWeeks(year)
    const monday = AgendaDate(year, Max(first + 7 * (week - 1), 0))
    const sunday = AgendaDate(year, first + 7 * week - 1)
    const thursday = AgendaDate(year, first + 7 * week - 4)
    const title = [monday, ...(sunday.month === monday.month ? [] : [sunday])]
      .map(({ month, year: y }) => `${MonthName(month, language)} ${y}`)
      .join(" / ")
      .toUpperCase()
    const font = Scale(pitch, 0.35)
    return [
      Area.contact(Slice.left(head, Scale(head.width, 0.4))[0], false),
      Area.text(
        Slice.top(head, Scale(head.height, 0.45))[0],
        title,
        PALETTE.primary,
        HorizontalAlign.RIGHT
      ),
      ...PlannerWeek(days, year, week, pitch, Scale(short, 0.02), language),
      Area.text(Slice.bottom(label, font)[0], `WEEK ${week}`),
      // Each month a strip, its name before it, as a strip calendar has it
      ...Slice.grid(strips, 1, 2, gap).flatMap((box, i) => {
        const { month, year: y } = ShiftMonth(thursday.year, thursday.month, i)
        const [name, strip] = Slice.left(box, Scale(box.width, 0.1), gap)
        return [
          Area.text(Slice.bottom(name, font)[0], MonthName(month, language), PALETTE.secondary),
          Area.strip(strip, month, y),
        ]
      }),
    ]
  },
  pages: "week",
}
